<template>
  <div>
    <button type="button" class="btn btn-primary btn-with-addon" @click="showModal">
      <span class="btn-addon">
        <i class="btn-addon-icon fe fe-plus-circle" />
      </span>
      Add Homework
    </button>
    <a-modal v-model="visible" title="Assign Homework" :width="this.settings['isMobileView'] ? 480 : 720"
      :destroyOnClose="true" :maskClosable="false">
      <template slot="footer">
        <a-button key="back" @click="handleCancel">Cancel</a-button>
        <a-button key="submit" type="primary" :loading="loading" @click="handleSubmit">Submit</a-button>
      </template>
      <a-form :form="form">
        <a-form-item label="Title">
          <a-input placeholder="Title" v-decorator="[
            'title',
            {
              rules: [
                { required: true, message: 'Please input a homework title!' },
              ],
            },
          ]" />
        </a-form-item>
        <a-form-item label="Description">
          <a-textarea placeholder="Description" :autoSize="{ minRows: 2 }" v-decorator="[
            'description',
            {
              rules: [
                { required: false, message: 'Please input a description!' },
              ],
            },
          ]" />
        </a-form-item>
        <p class="font-weight-bold mt-3">Homework Targets</p>
        <a-divider />
        <div class="row justify-content-center mt-3">
          <!-- Session -->
          <div class="col-md-4 col-12 mb-2">
            <p class="font-weight-bold text-dark mb-0">Session</p>
            <a-select size="default" v-model="selectedSession" optionFilterProp="children" style="width: 100%"
              @change="handleSessionChange" :filterOption="false" :defaultValue="currentSession ? currentSession.id : ''">
              <template v-for="schoolSession in schoolSessions">
                <a-select-option :value="schoolSession.id" :key="schoolSession.id">
                  {{ schoolSession.name }}
                  <small class="text-primary ml-2">{{
                    currentSession.id === schoolSession.id ? "Current" : ""
                  }}</small>
                </a-select-option>
              </template>
            </a-select>
          </div>
          <!-- Class -->
          <div class="col-md-4 col-12 mb-2">
            <p class="font-weight-bold text-dark mb-0">Class</p>
            <a-spin :spinning="classSpinning">
              <a-select size="default" v-model="selectedClass" placeholder="Select class" optionFilterProp="children"
                style="width: 100%" @change="handleClassChange" :filterOption="false" :defaultValue="undefined">
                <a-select-option :value="0">
                  Select Class
                </a-select-option>
                <template v-for="schoolClass in schoolClasses">
                  <a-select-option :value="schoolClass.id" :key="schoolClass.id">
                    {{ schoolClass.name }}
                  </a-select-option>
                </template>
              </a-select>
            </a-spin>
          </div>
          <!-- Section -->
          <div class="col-md-4 col-12 mb-2">
            <p class="font-weight-bold text-dark mb-0">Section</p>
            <a-spin :spinning="sectionSpinning">
              <a-select size="default" v-model="selectedSection" placeholder="Select section" optionFilterProp="children"
                style="width: 100%" :filterOption="false" :defaultValue="undefined" :disabled="!selectedClass">
                <a-select-option :value="0">
                  Select Section
                </a-select-option>
                <template v-for="section in sections">
                  <a-select-option :value="section.id" :key="section.id">
                    {{ section.name }}
                  </a-select-option>
                </template>
              </a-select>
            </a-spin>
          </div>
        </div>
        <div class="row justify-content-between">
          <div class="col-12 col-md-8">
            <p class="font-weight-bold text-dark mb-0">Subject</p>
            <a-spin :spinning="subjectSpinning">
              <a-select size="default" v-model="selectedSubject" optionFilterProp="children" style="width: 100%"
                :filterOption="false" :defaultValue="undefined" :disabled="!selectedClass">
                <a-select-option :value="0" disabled>Select Subject</a-select-option>
                <a-select-option v-for="subject in subjects" :value="subject.id" :key="subject.id">
                  {{ subject.name }}
                </a-select-option>
              </a-select>
            </a-spin>
          </div>
          <div class="col-12 col-md-4">
            <a-form-item label="Due Date">
              <a-date-picker v-decorator="[
                'due_date',
                {
                  rules: [
                    { required: false, message: 'Please input a date!' },
                  ],
                },
              ]" :format=dateFormat>
              </a-date-picker>
            </a-form-item>
          </div>
        </div>
        <p class="font-weight-bold mb-2 mt-3">Homework Files</p>
        <a-divider />
        <div class="w-25">
          <a-form-item class="mt-3">
            <a-upload v-decorator="['file', { rules: [{ required: false, message: 'Please input homework file.' }] }]"
              accept=".jpg,.jpeg,.png,.pdf,.xls,.xlsx,.doc,.docx, application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              :fileList="fileList" :multiple="false" :remove="handleRemove" :beforeUpload="beforeUpload"
              :disabled="uploadDisabled">
              <button type="button" class="btn btn-dark btn-with-addon">
                <span class="btn-addon">
                  <i class="btn-addon-icon fe fe-file-text" />
                </span>
                Attach File
              </button>
            </a-upload>
          </a-form-item>
        </div>
      </a-form>
    </a-modal>
  </div>
</template>
<script>
import moment from 'moment'
import { mapState } from 'vuex'
import { find, includes } from 'lodash'
export default {
  name: 'AddHomework',
  mounted: function () {
    this.$http
      .get('/api/session')
      .then(({ data }) => {
        this.schoolSessions = data.data
        this.selectedSession = find(data.data, ['is_current', 1]).id
      })
      .catch((error) => {
        console.log(error)
      })
    this.getClasses()
  },
  computed: {
    ...mapState(['settings', 'user']),
    currentSession: function () {
      const session = find(this.schoolSessions, ['is_current', 1])
      return session
    },
  },
  watch: {
    currentSession: function (val) {
      this.selectedSession = val.id
    },
  },
  data: function () {
    return {
      hasTargetError: false,
      hasTypeError: false,
      form: this.$form.createForm(this),
      loading: false,
      visible: false,
      data: [],
      schoolClasses: [],
      sections: [],
      subjects: [],
      schoolSessions: [],
      selectedClass: 0,
      selectedSection: 0,
      selectedSubject: 0,
      selectedSession: undefined,
      sectionSpinning: false,
      subjectSpinning: false,
      classSpinning: false,
      uploadDisabled: false,
      fileList: [],
      dateFormat: 'DD-MM-YYYY',
    }
  },
  methods: {
    includes,
    showModal() {
      this.visible = true
    },
    getClasses: function () {
      this.classSpinning = true
      this.$http
        .get('/api/class', {
          params: {
            session_id: this.selectedSession,
          },
        })
        .then(({ data }) => {
          this.schoolClasses = data.data
          this.classSpinning = false
        })
        .catch((error) => {
          console.log(error)
        })
    },
    handleClassChange (value) {
      this.selectedSection = 0
      this.selectedSubject = 0
      if (value !== null) {
        this.selectedClass = value
        this.sectionSpinning = true
        this.subjectSpinning = true
        this.getClassSections(value)
        this.getClassSubject(value)
      }
    },
    getClassSections(value) {
      this.$http
        .get(`/api/section/class/${value}`)
        .then(({ data }) => {
          this.sections = data.data
          this.sectionSpinning = false
        })
        .catch((error) => {
          console.log(error)
        })
    },
    getClassSubject(value) {
      this.$http
        .get(`/api/subject/class/${value}`)
        .then(({ data }) => {
          this.subjects = data.data.normal_subjects
          this.subjectSpinning = false
        })
        .catch((error) => {
          console.log(error)
        })
    },
    handleSessionChange(value) {
      if (value) {
        this.selectedSession = value
      } else {
        this.selectedSession = this.currentSession
      }

      this.selectedClass = 0
      this.selectedSection = 0
      this.selectedSubject = 0
      this.getClasses()
    },

    onTargetErrorClose() {
      this.hasTargetError = false
    },
    onTypeErrorClose() {
      this.hasTypeError = false
    },
    handleSubmit(e) {
      this.loading = true
      this.hasTargetError = false
      this.hasTypeError = false
      // eslint-disable-next-line handle-callback-err
      this.form.validateFields((err, values) => {
        this.$confirm({
          title: `Are you sure you want to send ${values.title}?`,
          content: `Click on the OK button to send a homework!`,
          onOk: () => {
            const formBody = {
              title: values.title,
              description: values.description,
              class_id: this.selectedClass,
              section_id: this.selectedSection,
              subject_id: this.selectedSubject,
              due_date: moment(values.due_date).format('YYYY-MM-DD'),
              files: this.fileList,
            }
            let formData = window.jsonToFormData(formBody)
            this.$http
              .post('/api/homework/assign', formData)
              .then(({ data }) => {
                this.$emit('created')
                this.loading = false
                this.visible = false
                this.$notification['success']({
                  message: 'Homework sent',
                  description: `Homework sent successfully!`,
                })
                this.form.resetFields()
                this.selectedClass = 0
                this.selectedSection = 0
                this.selectedSubject = 0
                this.fileList = []
              })
              .catch(error => {
                this.loading = false
                this.$notification['error']({
                  message: 'Unable to send homework!',
                  description: error.message,
                  duration: 0,
                })
              })
          },
          onCancel: () => {
            this.loading = false
          },
        })

        this.loading = false
      })
    },
    handleCancel(e) {
      this.visible = false
    },
    handleRemove(file) {
      const index = this.fileList.indexOf(file)
      const newFileList = this.fileList.slice()
      newFileList.splice(index, 1)
      this.fileList = newFileList
      this.uploadDisabled = false
    },
    beforeUpload(file) {
      this.fileList = [...this.fileList, file]
      const isJPG = file.type === 'image/jpeg'
      const isPNG = file.type === 'image/png'
      const isPDF = file.type === 'application/pdf'
      const isMsExcel =
        file.type === 'application/vnd.ms-excel' ||
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      const isMsWordDoc =
        file.type === 'application/msword' ||
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      if (!(isJPG || isPNG || isPDF || isMsExcel || isMsWordDoc)) {
        this.$message.error(
          'You can upload only JPG, PNG, Excel, MS Word or PDF file!'
        )
        this.handleRemove(file)
      }
      const isLt2M = file.size / 1024 / 1024 < 300
      if (!isLt2M) {
        this.$message.error('File must smaller than 300MB!')
        this.handleRemove(file)
      }
      // return isJPG && isLt2M
      // this.uploadDisabled = true
      return false
    },
  },
}
</script>
